import { ComponentProps, forwardRef, useMemo, useState } from 'react';

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@frontend/components/ui/avatar';
import { Input } from '@frontend/components/ui/input';
import { useDirectoryUsers } from '@frontend/shared/hooks/useDirectory';
import { useCombobox } from 'downshift';
import Fuse from 'fuse.js';

export type UserFieldExtensions = Pick<
  ComponentProps<typeof Input>,
  'placeholder' | 'onKeyDown' | 'disabled'
> & { isDisabled?: boolean };

export interface UserFieldInputProps extends UserFieldExtensions {
  onSelectId: (id: string) => void;
  selectedIds: string[];
  includedIds?: string[];
  excludedIds?: string[];
  onlyEditors?: boolean;
  excludeDeleted?: boolean;
}

type ShareItem = {
  id: string;
  name: string;
  image: string;
};

export const UserFieldInput = forwardRef<HTMLInputElement, UserFieldInputProps>(
  function UserFieldInput(
    {
      onSelectId,
      selectedIds,
      excludedIds,
      includedIds,
      // onlyEditors,
      excludeDeleted,
      ...inputProps
    },
    ref,
  ) {
    const _rawUsers = useDirectoryUsers();
    const [query, setQuery] = useState('');

    const users = useMemo(
      () =>
        _rawUsers
          .filter((i) => {
            if (excludedIds && excludedIds.includes(i.id)) {
              return false;
            }
            if (includedIds && !includedIds.includes(i.id)) {
              return false;
            }
            if (excludeDeleted && i.is_slack_deleted) {
              return false;
            }
            return true;
          })
          .map(
            (user) =>
              ({
                id: user.id,
                name: user.name,
                image: user.image,
              }) as ShareItem,
          ),
      [_rawUsers, excludedIds, includedIds, excludeDeleted],
    );

    const searchUsers = useMemo(
      () =>
        new Fuse(users, {
          keys: ['name'],
        }),
      [users],
    );

    const inputItems = useMemo(() => {
      if (query) {
        return searchUsers
          .search(query)
          .map((item) => item.item)
          .filter((item) => !selectedIds.find((id) => id === item.id));
      }

      return users.filter(
        (item: ShareItem) => !selectedIds.find((id) => id === item.id),
      );
    }, [users, selectedIds, query, searchUsers]);

    const ds = useCombobox({
      defaultHighlightedIndex: 0,
      items: inputItems,
      selectedItem: null,
      onSelectedItemChange: ({ selectedItem }) => {
        if (selectedItem) {
          onSelectId(selectedItem.id);
          ds.setInputValue('');
        }
      },
      onInputValueChange: ({ inputValue = '' }) => {
        if (typeof inputValue === 'string') {
          setQuery(inputValue);
        }
      },
    });

    return (
      <div className="relative h-8 w-[240px] flex items-center">
        <Input
          className="border-0 focus-visible:ring-0 shadow-none"
          {...ds.getInputProps({
            ref,
            ...inputProps,
            disabled: inputProps.isDisabled || inputProps.disabled,
          })}
        />
        <ul
          {...ds.getMenuProps()}
          className={`absolute top-full left-0 right-0 z-10 bg-white border border-gray-300 overflow-y-auto max-h-[210px] ${
            ds.isOpen && !!inputItems.length ? 'visible' : 'invisible'
          }`}
        >
          {ds.isOpen &&
            inputItems.map((item, index) => (
              <li
                key={item.id}
                className={`p-1 flex items-center cursor-pointer ${
                  ds.highlightedIndex === index ? 'bg-blue-100' : 'bg-white'
                }`}
                {...ds.getItemProps({ item, index })}
              >
                <Avatar className="mr-2 h-6 w-6">
                  <AvatarImage src={item.image} alt={item.name} />
                  <AvatarFallback>{item.name.charAt(0)}</AvatarFallback>
                </Avatar>
                {item.name}
              </li>
            ))}
        </ul>
      </div>
    );
  },
);
